import React from 'react';
import classes from './HeaderComponent.module.scss';
import { Badge, Segmented, Tooltip } from 'antd';
import { useAppDispatch, useAppSelector } from 'store/hook';
import cn from 'classnames';
import Assests from 'assets/icons';
import { useTranslation } from 'react-i18next';
import { setDocsSegment, setIsRSidebarCollapsed } from 'store/slices/windowStateSlice';
import { setReportSegment } from 'store/slices/researchSlice';
import Assets from 'assets/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAlignCenter, faChartPie, faCodeBranch } from '@fortawesome/free-solid-svg-icons';
import { audioExtensions, editableExtensions, extToAnalysis, videoExtensions } from 'helpers/filesExtensions';
import { setIsOpenAnalysisSider, setIsOpenAIAssistanceSider } from 'store/slices/aiFunctionsDataSlice';
import { widthOfSiderAnalytics } from 'components/DetailsComponent/AnalysisSider/AnalysisSider';

const isAvailableEditing = window.REACT_APP_IS_EDITING_ACTIVE !== 'REPLACE_REACT_APP_IS_EDITING_ACTIVE' ? window.REACT_APP_IS_EDITING_ACTIVE : process.env.REACT_APP_IS_EDITING_ACTIVE || 'false';
type SegmentSwitchTypes = {
  onSegmentCahnge: (value: string | number) => Promise<void>;
};

const SegmentSwitch = ({ onSegmentCahnge }: SegmentSwitchTypes) => {
  const [segmentedKey, setSegmentedKey] = React.useState(0);

  const { selectedKeys, isLoadingProcess, docsSegment, rSidebarEntity } = useAppSelector((state) => state.windowState);
  const { reportSegment, isReportDocumentHasNoToken } = useAppSelector((state) => state.research);
  const { selectedDocument, viewType, currentFolder, selectedVersion } = useAppSelector((state) => state.documents);
  const { available_rooms, userData } = useAppSelector((store) => store.userData);
  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  const { notes } = useAppSelector((state) => state.notes);
  const { isOpenAnalysisSider } = useAppSelector((state) => state.aiFunctions);

  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const currentRoom = available_rooms.find((room) => room.id === dataRoom?.id);
  const isActiveUser = !dataRoom?.is_blocked;
  const isDeletedFile = selectedDocument?.deleted_at;
  const isAvailableEditMode = isAvailableEditing === 'true' && selectedDocument?.permissions?.includes('can_edit_document');
  const normalizedName = selectedDocument?.extension?.toLocaleLowerCase() || '';
  const isEditableFormat = editableExtensions.includes(normalizedName);
  const isTranscribedFormat = [...audioExtensions, ...videoExtensions].includes(normalizedName);
  const isExperimentalAvailable = userData?.is_experimental_available;
  const isRecycleBin = selectedKeys[0] === 'recycleBin';
  const isAvailableToAnalysis = extToAnalysis.includes(normalizedName);
  const rightDocsSegment = isOpenAnalysisSider ? 'analysis' : docsSegment;
  const isChoseOldVersion = Boolean(selectedVersion?.id);

  React.useEffect(() => {
    const segmentDocuments = document.querySelector('.segment-option-documents');
    const segmentUsers = document.querySelector('.segment-option-users');
    if (segmentDocuments) segmentDocuments.setAttribute('data-testid', 'room-tab-files')
    if (segmentUsers) segmentUsers.setAttribute('data-testid', 'room-tab-users')
  }, []);

  React.useEffect(() => {
    setTimeout(() => setSegmentedKey(prev => prev + 1), 50);
  }, [rSidebarEntity]);

  const onChangeDocsSegment = (value: string) => {
    if (value !== 'edit') {
      dispatch(setIsOpenAIAssistanceSider({ open: false, width: 0 }));
    };
    if (value !== 'view') {
      dispatch(setIsRSidebarCollapsed({collapsed: true, entity: '', width: 0}));
    };
    
    if (value === 'analysis') {
      const spaceForDoc = window.innerWidth - Number(localStorage.getItem('sideBarWidth'));
      dispatch(setIsOpenAnalysisSider({ open: true, width: spaceForDoc < 840 ? widthOfSiderAnalytics[2] : widthOfSiderAnalytics[1]  }));
      docsSegment !== 'view' && dispatch(setDocsSegment('view'));
      dispatch(setIsRSidebarCollapsed({collapsed: true, entity: '', width: 0}));
    } else {
      dispatch(setIsOpenAnalysisSider({ open: false, width: 0 }));
      dispatch(setDocsSegment(value));
    }
  };
  const onChangeReportSegment = (value: string) => dispatch(setReportSegment(value as 'info' | 'changes' | 'actions'));

  const tabsOptions = React.useMemo(() => {
    let optionItems = [
      {
        className: 'segment-option-documents',
        label: (
          <span>
            <Assests.FileIcon className={classes.tabIcon} />
            <span className={classes.tabText}>{t('sidebar.documents')}</span>
            {currentRoom && currentRoom?.documents_amount && isActiveUser && (
              <>
                <span className={classes.tabCount}>
                  {currentRoom?.documents_amount}
                </span>
                {Boolean(currentRoom?.new_count) && isActiveUser && (
                  <Tooltip title={t('Rooms.badges.new_files')}>
                    {
                      <span className={classes.tabCountNew}>
                        +{currentRoom?.new_count}
                      </span>
                    }
                  </Tooltip>
                )}
              </>
            )}
          </span>
        ),
        value: 'documents',
      },
      {
        label: (
          <span>
            <Assests.ChatIcon className={classes.tabIcon} />
            <span className={classes.tabText}>{t('sidebar.chat')}</span>
            {currentRoom &&
              Boolean(currentRoom.unviewed_amount) &&
              currentRoom.unviewed_amount &&
              currentRoom.unviewed_amount > 0 &&
              isActiveUser && (
                <Tooltip title={t('Rooms.badges.new_messages')}>
                  {
                    <span className={classes.tabCountNew}>
                      +{Math.max(Number(currentRoom.unviewed_amount), 0)}
                    </span>
                  }
                </Tooltip>
              )}
          </span>
        ),
        value: 'chat',
      },
      {
        className: 'segment-option-users',
        label: (
          <span>
            <Assests.MembersIcon className={classes.tabIcon} />
            <span className={classes.tabText}>{t('sidebar.users')}</span>
            {currentRoom &&
              currentRoom?.rooms_constraints?.members_amount &&
              isActiveUser && (
                <span className={classes.tabCount}>
                  {Number(currentRoom?.rooms_constraints?.members_amount)}
                </span>
              )}
          </span>
        ),
        value: 'users',
      },
    ];
    return optionItems;
  }, [dataRoom, i18n.language, available_rooms]);

  const docOptions = React.useMemo(() => {
    let optionItems = [
      {
        label: (
          <span>
            <Assets.VisibleIcon className={classes.tabIcon} />
            <span className={classes.tabText}>
              {t('Documents.table.columns.view')}
            </span>
          </span>
        ),
        value: 'view',
      },
    ];
    !isChoseOldVersion && optionItems.push({
      label: (
        <span>
          <Assets.NoteIcon className={classes.tabIcon} />
          <span className={classes.tabText}>
            {t('Documents.table.columns.notes')}
          </span>
          <span className={classes.tabCount}>
            {notes?.length
              ? notes?.length
              : selectedDocument?.has_notes && (<Badge dot/>)}
          </span>
        </span>
      ),
      value: 'notes',
    });
    !isChoseOldVersion && optionItems.push({
      label: (
        <span>
          <Assests.ChatIcon className={classes.tabIcon} />
          <span className={classes.tabText}>{t('sidebar.chat')}</span>
          {selectedDocument?.unviewed_amount && selectedDocument?.unviewed_amount > 0 ? (
            <span className={classes.unviewed}>
              +{selectedDocument?.unviewed_amount}
            </span>
          ) : selectedDocument?.message_amount ? (
            <span className={classes.tabCount}>
              {selectedDocument?.message_amount}
            </span>
          ) : null}
        </span>
      ),
      value: 'chat',
    });
    optionItems.push({
      label: (
        <span>
          <FontAwesomeIcon className={classes.faIcon} icon={faCodeBranch} />
          <span className={classes.tabText}>
            {t('Documents.details.versions.title')}
          </span>
          {Number(selectedDocument?.version_count) > 1 && (
            <span className={classes.tabCount}>
              {Number(selectedDocument?.version_count) - 1}
            </span>
          )}
        </span>
      ),
      value: 'versions',
    });
    !isChoseOldVersion && optionItems.push({
      label: (
        <span>
          <Assets.LogIcon className={classes.tabIcon} />
          <span className={classes.tabText}>
            {t('Documents.details.timeline')}
          </span>
        </span>
      ),
      value: 'timeline',
    });

    !isChoseOldVersion && isAvailableToAnalysis && isExperimentalAvailable && optionItems.splice(1, 0, {
        label: (
          <span>
            <FontAwesomeIcon className={classes.tabFaIcon} icon={faChartPie}/>
            <span className={classes.tabText}>
              {t('Documents.details.analysis')}
            </span>
          </span>
        ),
        value: 'analysis',
      });

    !isChoseOldVersion && isEditableFormat && isAvailableEditMode && // && selectedDocument?.signing_status === null
      optionItems.push({
        label: (
          <span>
            <Assets.FileEditIcon className={classes.tabIcon} />
            <span className={classes.tabText}>
              {t('Documents.details.toEdit')}
            </span>
          </span>
        ),
        value: 'edit',
      });
      if(isRecycleBin) return [optionItems[0]];

    !isChoseOldVersion && isTranscribedFormat && isExperimentalAvailable && !selectedDocument?.is_confidential
      && optionItems.push({
        label: (
          <span>
            <FontAwesomeIcon className={classes.tabFaIcon} rotation={270} icon={faAlignCenter}/>
            <span className={classes.tabText}>
              {t('Documents.table.columns.transcription')}
            </span>
          </span>
        ),
        value: 'transcription',
      });

    return optionItems;
  }, [selectedDocument?.id, i18n.language, notes, currentFolder, isRecycleBin, selectedVersion?.id]);

  const reportOptions = React.useMemo(() => {
    return [
      {
        label: (
          <span>
            <Assests.FileIcon className={classes.tabIcon} />
            <span className={classes.tabText}>{t('Report.tabs.info')}</span>
          </span>
        ),
        value: 'info',
      },
      {
        label: (
          <span>
            <FontAwesomeIcon className={classes.faIcon} icon={faCodeBranch} />
            <span className={classes.tabText}>{t('Report.tabs.changes')}</span>
          </span>
        ),
        value: 'changes',
        disabled: isReportDocumentHasNoToken,
      },
      {
        label: (
          <span>
            <Assests.LogIcon className={classes.tabIcon} />
            <span className={classes.tabText}>{t('Report.tabs.action')}</span>
          </span>
        ),
        value: 'actions',
        disabled: isReportDocumentHasNoToken,
      },
    ];
  }, [t, isReportDocumentHasNoToken]);

  const displayedContent = {
    Table: (
      <div className={classes.tabsWrap}>
        <Segmented
          className={cn(
            classes.tabs,
            isLoadingProcess ? classes.loading : 'null'
          )}
          options={tabsOptions}
          block
          disabled={
            !Boolean(dataRoom?.id) ||
            dataRoom?.is_blocked ||
            dataRoom?.access_terms_status === 'waiting_for_approve'
          }
          selected
          value={selectedKeys[0] === 'rooms' ? 'documents' : selectedKeys[0]}
          onChange={onSegmentCahnge}
        />
      </div>
    ),
    Document: (
      <div className={classes.tabsWrap}>
        <Segmented
          className={cn(
            classes.tabs,
            isLoadingProcess ? classes.loading : 'null'
          )}
          options={docOptions}
          block
          key={segmentedKey}
          selected
          value={rightDocsSegment}
          disabled={Boolean(isDeletedFile) || Boolean(!selectedDocument)}
          onChange={onChangeDocsSegment}
        />
      </div>
    ),
    Report: (
      <div className={classes.tabsWrap}>
        <Segmented
          className={cn(
            classes.tabs,
            isLoadingProcess ? classes.loading : 'null'
          )}
          options={reportOptions}
          block
          selected
          value={reportSegment}
          onChange={onChangeReportSegment}
        />
      </div>
    ),
  };

  return displayedContent[viewType];
};

export default SegmentSwitch;
